import React, {
  useCallback, useContext, useState, useEffect,
} from 'react';
import {
  Badge,
  Button,
  Collapsible, Icon,
  LinkAsButton,
  RadioCardGroup,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  SupportArticleModal,
  useSelectorMachineContext,
  useSelectorMachineSend, VatModeContext,
} from '@printdeal/product-selector';
import { SERVICES } from '@printdeal/business-logic';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import cx from 'clsx';
import { dispatchDataLayerEntry } from '../../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { setInLocalStorage } from '../../../../../helpers/window';
import DetermineDeliveryTool from '../../../detail/DetermineDeliveryTool';
import type { SelectedDeliveryTool } from '../../../../../types/file-delivery/FileDelivery';
import { hasServiceKey } from '../../../detail/advanced/FileServicesSection';
import { Money } from '../../../../Money';
import { FileDeliveryAndServiceProps } from './file-delivery-and-service-types';
import { useScrollToSummary } from '../../../../../hooks/useScrollOnPriceSelect';

export const FileDeliveryAndServicesExperiment = ({
  productPage,
  selector,
  hasFileDeliveryChoice,
  setSelectedDeliveryTool,
  selectedDeliveryTool,
  services,
  setSelectedServices,
  selectedServices,
  setIsChoiceSkipped,
  alternativeUploadService,
  setAlternativeUploadService = () => {},
}: FileDeliveryAndServiceProps) => {
  const intl = useIntl();
  const send = useSelectorMachineSend();
  const { vatMode } = useContext(VatModeContext);
  const merchantRules = useSelectorMachineContext((state) => state.context.merchantRules);
  const [isOpen, setIsOpen] = useState(true);
  const [isFileServiceOpen, setIsFileServiceOpen] = useState(true);
  const [showSupportArticleModal, setShowSupportArticleModal] = useState(false);

  const { deliveryTool: merchantDeliveryTool } = merchantRules;
  const defaultDeliveryTool = {
    // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
    checkmate: 'on',
    editor: 'on',
  };

  const deliveryTool = { ...defaultDeliveryTool, ...merchantDeliveryTool };

  // Services
  const fileDesignService = services.find((service) => service.name === SERVICES.FILE_DESIGN);
  const fileCheckService = services.find((service) => service.name === SERVICES.FILE_CHECK);
  const fileChangeService = services.find((service) => service.name === SERVICES.FILE_CHANGE);
  const isFileDesignServiceSelected = fileDesignService && selectedServices.includes(fileDesignService.key);
  const hasFileCheckService = fileCheckService && selectedServices.includes(fileCheckService.key);
  const isFileChangeServiceSelected = fileChangeService && selectedServices.includes(fileChangeService.key);
  const serviceKeys = merchantDeliveryTool.servicekeys?.split(',') || [];
  const showFileCheckService = fileCheckService && hasServiceKey(serviceKeys, fileCheckService);
  const isFileDesignServiceEnabled = fileDesignService && hasServiceKey(serviceKeys, fileDesignService);
  const isFileChangeServiceEnabled = fileChangeService && hasServiceKey(serviceKeys, fileChangeService);
  const supportArticleId = fileCheckService?.readMore?.contentful_id;

  const price = useSelectorMachineContext((state) => state.context.price);
  const { deliveryChoiceRef, fileCheckSelectionRef } = useScrollToSummary({
    selectedPrice: price,
    selectedDeliveryTool,
    isExp1862Variant: true,
    alternativeUploadService: !!alternativeUploadService,
  });

  const isDirectUpload = Boolean(deliveryTool?.directupload);
  // don't show the file services when 'directUpload' is true, and this will only be configured in MRC for exp1692
  const hasServices = deliveryTool?.servicekeys && services.length > 0 && !isDirectUpload;

  const handleToggleSupportArticleModal = useCallback((value: boolean) => {
    setShowSupportArticleModal(value);
    dispatchDataLayerEntry(
      'EXP1862 - Rerun Automatic file check and Manual paid file check',
      'click',
      {
        event: 'file_check_section',
        design_choice: 'manual_file_check_more_info',
        eventNonInteraction: false,
      },
    );
  }, []);

  const handleDeliveryToolChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedDeliveryTool(value as SelectedDeliveryTool);
    setInLocalStorage('deliveryTool', value);
    dispatchDataLayerEntry(
      'select_file_delivery_tool',
      'click',
      {
        deliver_tool: deliveryTool,
        product_name: productPage.productNameOriginal,
        contentfulId: productPage.contentful_id,
        variant: selector.configuration.variantSku,
      },
    );
  };

  const handleServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value, id } = event.target;
    if (setIsChoiceSkipped) {
      setIsChoiceSkipped(true);
    }
    setSelectedServices(checked
      ? [value]
      : selectedServices.filter((service) => service !== value));

    send({
      type: 'Select service',
      selectedService: checked ? value : '',
    });

    // Dispatch data layer event with the appropriate event action
    addDataLayerEntry('click_service_selection', {
      eventCategory: 'product_detail_page',
      eventAction: checked ? 'service_selected' : 'service_deselected',
      serviceKey: id,
      eventNonInteraction: false,
    });
  };

  const handleFileServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    const isServiceSelected = value !== 'no-service' && checked;

    // Set the selected service and update state accordingly
    setAlternativeUploadService(value);
    setSelectedServices(isServiceSelected ? [value] : []);

    // Send the selected service information
    send({ type: 'Select service', selectedService: isServiceSelected ? value : '' });

    // Dispatch data layer entry with the appropriate event action
    dispatchDataLayerEntry(
      'EXP1862 - Rerun Automatic file check and Manual paid file check',
      'click',
      {
        event: 'file_check_section',
        design_choice: isServiceSelected ? 'manual_file_check' : 'automatic_file_check',
        eventNonInteraction: false,
      },
    );
  };

  const filterService = useCallback((serviceKey: string) => {
    const filtered = selectedServices.filter((service) => service !== serviceKey);
    setSelectedServices(filtered);
  }, [selectedServices, setSelectedServices]);

  const handleServiceSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleServiceChange(event);
    if (setSelectedDeliveryTool) {
      setSelectedDeliveryTool(undefined);
    }

    if (alternativeUploadService) {
      setAlternativeUploadService(undefined);
    }

    addDataLayerEntry('select_design_service', {
      selected: event.target.checked,
      title: event.target.id,
    });
  };

  useEffect(() => {
    send({
      type: 'Select service',
      selectedService: selectedServices.length > 0 ? selectedServices[0] : '',
    });
  }, [selectedServices, send]);

  useEffect(() => {
    if (selectedDeliveryTool && isFileDesignServiceSelected) {
      // When a delivery tool is selected,
      // the design service should be deselected because they are mutually exclusive
      filterService(fileDesignService.key);
    }
    if (selectedDeliveryTool && isFileChangeServiceSelected) {
      // When a delivery tool is selected,
      // the change service should be deselected because they are mutually exclusive
      filterService(fileChangeService.key);
    }

    if (hasFileCheckService && (isFileDesignServiceSelected || isFileChangeServiceSelected)) {
      // When a file check service is selected,
      // the design service should be deselected because they are mutually exclusive
      filterService(fileCheckService.key);
    }
  }, [
    selectedDeliveryTool,
    hasFileCheckService,
    isFileDesignServiceSelected,
    filterService,
    fileDesignService,
    fileCheckService,
    fileChangeService,
    isFileChangeServiceSelected,
  ]);

  const handleSkipDeliveryToolChoice = () => {
    setIsChoiceSkipped(true);
    setSelectedDeliveryTool(undefined);
    setAlternativeUploadService(undefined);
    setSelectedServices([]);
    setSelectedServices([]);
    setIsOpen(false);
    send({ type: 'Select service', selectedService: '' });
    dispatchDataLayerEntry(
      'select_file_delivery_tool',
      'click',
      {
        deliver_tool: null,
        product_name: selector.product.productNameOriginal,
        contentfulId: selector.id,
        variant: selector.configuration.variantSku,
      },
    );
  };

  if (!hasServices && !hasFileDeliveryChoice) {
    // Duplicating it here rather than lifting it up into FileDeliveryAndServiceWrapper, because...
    // ... both the control-component and variant-component have useEffects which appears to be non-optional.
    return null;
  }

  return (
    <div className="pd-my-6 pd-flex pd-flex-col pd-gap-3">
      <div ref={deliveryChoiceRef} className="pd-scroll-mt-[750px] md:pd-scroll-mt-24 xl:pd-scroll-mt-64">
        <Collapsible id="delivery-tool-collapsible-experiment" isOpen={isOpen}>
          <Collapsible.Header
            button={<Collapsible.Button onClick={() => setIsOpen(!isOpen)} />}
            title={(
              <FormattedMessage
                id="product.configurator.determineDeliveryTool"
                defaultMessage="How are you going deliver your design?"
              />
          )}
          >
            <FormattedMessage
              id="product.configurator.determineDeliveryTool"
              defaultMessage="How are you going deliver your design?"
            />
          </Collapsible.Header>

          <Collapsible.Panel className="pd-flex pd-flex-col pd-gap-5">
            {hasFileDeliveryChoice && (
              <DetermineDeliveryTool
                onToolChange={handleDeliveryToolChange}
                selectedDeliveryTool={selectedDeliveryTool}
                // "Checkmatemode: checkmate" and "checkmate: on" will be renamed
                // when MRC is replaced by the business rules app
                isUploaderEnabled={deliveryTool.checkmate === 'on'}
                isEditorEnabled={deliveryTool.editor === 'on'}
                isAltUploadBeforeOrderEnabled={deliveryTool.altuploadbeforeorder === 'on'}
                servicesEnabled={Boolean(services)}
                isFileDesignServiceEnabled={isFileDesignServiceEnabled}
                fileDesignService={fileDesignService}
                handleServiceSelection={handleServiceSelection}
                isFileDesignServiceSelected={isFileDesignServiceSelected}
                isFileChangeServiceEnabled={isFileChangeServiceEnabled}
                isFileChangeServiceSelected={isFileChangeServiceSelected}
                fileChangeService={fileChangeService}
              />
            )}
            <div className="pd-flex pd-items-center pd-gap-2 pd-self-end">
              <span className="pd-text-dim-500">
                <FormattedMessage
                  id="delivery.tool.skipChoice.title"
                  defaultMessage="You don't want to add your design until later?"
                />
              </span>
              <Button variant="dim" onClick={handleSkipDeliveryToolChoice} iconBefore="arrow-right">
                <FormattedMessage id="delivery.tool.skipChoice.button" defaultMessage="Skip choice" />
              </Button>
            </div>
          </Collapsible.Panel>
        </Collapsible>
      </div>
      {selectedDeliveryTool && !isFileDesignServiceSelected && showFileCheckService && (
        <div ref={fileCheckSelectionRef}>
          <SupportArticleModal
            supportArticleId={supportArticleId}
            show={showSupportArticleModal}
            onClose={() => handleToggleSupportArticleModal(false)}
          />
          <Collapsible id="file-check-service-experiment" isOpen={isFileServiceOpen} className="pd-mt-5">
            <Collapsible.Header
              button={<Collapsible.Button onClick={() => setIsFileServiceOpen(!isFileServiceOpen)} />}
              title={(
                <FormattedMessage
                  id="product.detail.fileCheckOptions.header.title"
                  defaultMessage="Design Check"
                />
              )}
            >
              <FormattedMessage
                id="product.detail.fileCheckOptions.header.description"
                defaultMessage="Manual design check by a specialist"
              />
            </Collapsible.Header>
            <Collapsible.Panel>
              <div className="pd-flex pd-flex-col pd-gap-5">
                <RadioCardGroup
                  id="alternative-upload-services"
                  name="alternative-upload-services"
                >
                  <RadioCardGroup.Fields className="pd-grid-cols-2">
                    <RadioCardGroup.Field
                      id="no-service"
                      value="no-service"
                      checked={alternativeUploadService === 'no-service'}
                      onChange={handleFileServiceChange}
                      label={<FormattedMessage id="product.detail.fileCheckOptions.automatic" defaultMessage="Automatic file check" />}
                      description={(
                        <>
                          <p className="pd-mb-2">
                            <FormattedMessage id="checkout.payment.fileCheck.automatic.free" defaultMessage="Free" />
                          </p>
                          <p>
                            <FormattedMessage
                              id="product.detail.fileCheckOptions.automatic.description"
                              defaultMessage="Our system checks your print-ready PDF-file automatically on a couple of relevant technological factors."
                            />
                          </p>
                        </>
                        )}
                      image={(
                        <Icon
                          icon="check"
                          size="2xl"
                          className={cx({
                            'pd-text-primary-500': alternativeUploadService === 'no-service',
                          })}
                        />
                        )}
                    />
                    <RadioCardGroup.Field
                      id={fileCheckService.key}
                      value={fileCheckService.key}
                      checked={alternativeUploadService === fileCheckService.key}
                      onChange={handleFileServiceChange}
                      label={<FormattedMessage id="product.detail.fileCheckOptions.specialist" defaultMessage="Manual design check by a specialist" />}
                      description={(
                        <div className="pd-flex pd-flex-col pd-items-start pd-gap-2">
                          {fileCheckService.price && (
                            <p className="pd-whitespace-nowrap">
                              + <Money
                                value={fileCheckService.price[vatMode].centAmount}
                                currency={fileCheckService.price[vatMode].currencyCode}
                              />
                            </p>
                          )}

                          <FormattedMessage
                            id="product.detail.fileCheckOptions.specialist.description"
                            defaultMessage="Our specialists check for resolution, size, positioning, rotation, folding, readability, cutouts and line thicknesses and adjust your design as needed."
                          />
                          <LinkAsButton
                            className="pd-text-sm pd-font-normal"
                            variant="secondary"
                            onClick={() => handleToggleSupportArticleModal(true)}
                            iconBefore="arrow-right"
                            screenReaderLabel={intl.formatMessage({ id: 'service.filecheck.secondaryAction.screenReaderLabel', defaultMessage: 'More Information' })}
                          >
                            <FormattedMessage id="services.label.moreInfo" defaultMessage="More Info" />
                          </LinkAsButton>
                          <Badge variant="success">
                            <FormattedMessage id="product.detail.fileCheckOptions.specialist.badge.label" defaultMessage="For extra insurance" />
                          </Badge>
                        </div>
                        )}
                      image={(
                        <Icon
                          icon="eye-check"
                          size="2xl"
                          className={cx({
                            'pd-text-primary-500': alternativeUploadService === fileCheckService.key,
                          })}
                        />
                        )}
                    />
                  </RadioCardGroup.Fields>
                </RadioCardGroup>
              </div>
            </Collapsible.Panel>
          </Collapsible>
        </div>
      )}
    </div>
  );
};
